// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import { inject, Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { gql, QueryOptions } from '@apollo/client/core';
import { lastValueFrom, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { isOffline } from '../../../utils/connection.service';
import { OfflineDb } from '../../../store/offline-db/offline.db';

export type GqlQueryOptions = Omit<QueryOptions, 'query' | 'variables'> & { offline?: boolean };
type mutateOpts = { delay?: boolean; offline?: boolean };

@Injectable()
export abstract class GqlServiceV2 {
    protected offlineDb = inject(OfflineDb);
    protected apollo = inject(Apollo);

    protected regex = new RegExp(
        /\{\s*([a-zA-Z0-9]+)\s*\{\s*([a-zA-Z0-9]+)?/,
        'gm',
    );
    protected latest = {} as Record<string, Record<string, number>>;

    protected async query<T>(
        query: string,
        variables: object = {},
        options: GqlQueryOptions = {},
    ): Promise<T> {
        const [resource, method] = this.getFields(query);
        const { offline = true, context, ...queryOptions } = options;

        if (isOffline() && offline) {
            if (method === 'filter') {
                return this.offlineDb.filter(resource, variables?.input || {});
            } else {
                return this.offlineDb.get(
                    resource,
                    method,
                    variables?.input || {},
                );
            }
        }

        return lastValueFrom(
            this.apollo
                .query({
                    query: gql`
                        ${query}
                    `,
                    variables,
                    context: {
                        queryDeduplication: false,
                        ...context,
                    },
                    ...queryOptions,
                })
                .pipe(
                    map((result) => {
                        const res = result.data?.[resource]?.[method] ?? null;
                        if (res && typeof res === 'object') {
                            const lastTs = this.latest[resource]?.[method] || 0;
                            res.__ts = result?.data?.__ts || 0;
                            res.__latest = res.__ts > lastTs;
                            if (res.__latest) {
                                this.latest[resource] ??= {};
                                this.latest[resource][method] = lastTs;
                                this.offlineDb.store(resource, method, res);
                            }
                        }

                        return res;
                    }),
                ),
        ).catch((e) => {
            throw new Error(e.message);
        });
    }

    protected mutate<T>(
        mutation: string,
        variables: object = {},
        opts: mutateOpts = {},
    ): Promise<T> {
        const [resource, method] = this.getFields(mutation);

        if (isOffline() && opts.offline !== false) {
            if (opts?.delay) {
                this.offlineDb
                    .delay(resource, method, variables?.input || {})
                    .then();
            }
            return new Promise((res) => res(null));
        }

        return lastValueFrom(
            this.apollo
                .mutate({
                    mutation: gql`
                        ${mutation}
                    `,
                    variables,
                })
                .pipe(
                    map((result) => result.data![resource][method]),
                    catchError((error) => throwError(() => new Error(error))),
                ),
        );
    }

    protected getFields(query: string): string[] {
        return query.matchAll(this.regex).next().value.slice(1);
    }
}
